<template>
    <span class="bo-line-discount" :class="{ 'bo-line-discount--all': showAll }">
        <span
            class="bo-line-discount__values"
            :class="{ null: steps.length === 0 }"
        >
            {{ steps.length > 0 ? visibleSteps.join(" ") : "-" }}
        </span>

        <span
            class="bo-line-discount__more"
            :aria-hidden="hiddenSteps.length === 0 ? 'true' : null"
        >
            <i
                v-if="hiddenSteps.length > 0"
                ref="icon"
                class="icon-info"
                tabindex="0"
                :aria-label="steps.join(' ')"
                @mouseenter="showTooltip"
                @mouseleave="hideTooltip"
                @focus="showTooltip"
                @blur="hideTooltip"
            ></i>
        </span>

        <!-- position: fixed so the tooltip is not clipped by the datatable's overflow. -->
        <span
            v-if="tooltip"
            class="bo-line-discount__tooltip"
            :class="{ 'bo-line-discount__tooltip--above': tooltip.above }"
            role="tooltip"
            :style="{ top: tooltip.top, left: tooltip.left, '--arrow-left': tooltip.arrowLeft }"
        >
            <span class="bo-line-discount__tooltip-content">
                <span
                    v-if="title"
                    class="bo-line-discount__tooltip-title"
                    >{{ title }}</span
                >
                <span class="bo-line-discount__tooltip-steps">
                    <span
                        v-for="(step, index) in steps"
                        :key="index"
                        >{{ step }}</span
                    >
                </span>
            </span>
        </span>
    </span>
</template>

<script>
// Line item discount levels: the first MAX_VISIBLE inline; when there are more, a hover tooltip lists every level.
// With showAll every level is shown inline and there is no tooltip.
const MAX_VISIBLE = 2;
const TOOLTIP_WIDTH = 256;
// Space between the icon and the arrow tip, plus the arrow's height.
const TOOLTIP_OFFSET = 4 + 6;
const VIEWPORT_MARGIN = 8;
// Rough tooltip height; below this much room under the icon the tooltip opens above it instead.
const MIN_SPACE_BELOW = 120;

export default {
    props: {
        // Pre-formatted levels in applied order, e.g. ["-RM 5.00", "-2.50%", "-RM 1.00"].
        steps: {
            type: Array,
            default: () => [],
        },
        showAll: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            // General translations are loaded on every storefront page (cart, documents, catalog).
            default: () => window.bo?.generalTranslations?.all_discounts ?? "",
        },
    },

    data() {
        return {
            tooltip: null,
        };
    },

    computed: {
        visibleSteps() {
            return this.showAll ? this.steps : this.steps.slice(0, MAX_VISIBLE);
        },
        hiddenSteps() {
            return this.showAll ? [] : this.steps.slice(MAX_VISIBLE);
        },
    },

    methods: {
        showTooltip() {
            const rect = this.$refs.icon.getBoundingClientRect();
            const iconCenter = rect.left + rect.width / 2;
            // Centred under the icon, kept inside the viewport; the arrow follows the icon.
            const maxLeft = window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN;
            const left = Math.max(VIEWPORT_MARGIN, Math.min(iconCenter - TOOLTIP_WIDTH / 2, maxLeft));
            const above = window.innerHeight - rect.bottom < MIN_SPACE_BELOW && rect.top > MIN_SPACE_BELOW;

            this.tooltip = {
                above,
                top: `${above ? rect.top - TOOLTIP_OFFSET : rect.bottom + TOOLTIP_OFFSET}px`,
                left: `${left}px`,
                arrowLeft: `${iconCenter - left}px`,
            };
        },
        hideTooltip() {
            this.tooltip = null;
        },
    },
};
</script>

<style lang="scss" scoped>
// Left-aligned, like the Discount column header.
.bo-line-discount {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    white-space: nowrap;

    &--table {
        width: 100%;
    }

    &--all {
        width: auto;

        .bo-line-discount__more {
            display: none;
        }
    }
}

.bo-line-discount__more {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 16px;
    width: 16px;
    margin-left: 4px;

    i {
        font-size: 14px;
        cursor: pointer;
        outline: none;
    }
}

// In a table the icon slot is reserved on every row so the discounts line up, but only when some row
// actually has more than MAX_VISIBLE levels (an info icon); otherwise no row keeps the empty space.
tbody:not(:has(.bo-line-discount__more i)) .bo-line-discount--table .bo-line-discount__more {
    display: none;
}

// Tooltip - M: 256px wide, 8px padding, 4px radius, Elevation/E2.
.bo-line-discount__tooltip {
    position: fixed;
    z-index: 1070;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 256px;
    padding: 8px;
    border-radius: 4px;
    background: #424242;
    color: #ffffff;
    box-shadow: 0px 2px 6px 2px rgba(0, 0, 0, 0.08);
    filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.16));
    text-align: left;
    white-space: normal;
    pointer-events: none;

    // Arrow pointing at the icon: on top when the tooltip is below it, at the bottom when above.
    &::before {
        content: "";
        position: absolute;
        bottom: 100%;
        left: var(--arrow-left);
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top: 0;
        border-bottom-color: #424242;
    }

    &--above {
        transform: translateY(-100%);

        &::before {
            top: 100%;
            bottom: auto;
            border-top: 6px solid #424242;
            border-bottom: 0;
        }
    }
}

.bo-line-discount__tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    padding: 4px;
}

.bo-line-discount__tooltip-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
}

// Levels flow inline and wrap; a single level never breaks across lines.
.bo-line-discount__tooltip-steps {
    display: flex;
    flex-wrap: wrap;
    gap: 0 4px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;

    span {
        white-space: nowrap;
    }
}
</style>
